import * as React from 'react'

import { Input } from './input'
import { Label } from './label'
import { Thumbnail } from '../identity/thumbnail'
import { cn } from '../lib/cn'
import { menuItemBase, popoverSurface } from '../lib/surfaces'

// Pick one item out of a list someone else searched: no matcher, no Popover, `onSelect` an action
// and not a binding. Why: packages/ui/docs/trappole.md § «Combobox: niente matcher, niente Popover»

export interface ComboboxOption {
  /** Opaque to the design system: the caller's id, stringified. */
  readonly value: string
  readonly label: string
  /** A second line, for telling two identically named options apart. */
  readonly hint?: string
  /** Identity imagery beside the label — a club's crest. Absent draws the fallback. */
  readonly imageUrl?: string
}

export interface ComboboxProps {
  /** Visible label. Required: an unnamed search field is unusable by screen reader. */
  readonly label: string
  readonly options: readonly ComboboxOption[]
  readonly inputValue: string
  readonly onInputValueChange: (next: string) => void
  readonly onSelect: (value: string) => void
  readonly loading?: boolean
  /** Announced while the options are in flight. */
  readonly loadingLabel: string
  /** Shown when the search matched nothing. */
  readonly emptyLabel: string
  readonly placeholder?: string
  readonly disabled?: boolean
  readonly id?: string
  readonly className?: string
  /** Wiring for an error owned by the caller — the same two attributes
   *  `FormFieldControlProps` hands a control, applied here to the actual `<input>`. */
  readonly 'aria-describedby'?: string
  readonly 'aria-invalid'?: boolean
}

const NONE_ACTIVE = -1

export function Combobox({
  label,
  options,
  inputValue,
  onInputValueChange,
  onSelect,
  loading = false,
  loadingLabel,
  emptyLabel,
  placeholder,
  disabled = false,
  id,
  className,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: ComboboxProps) {
  const generatedId = React.useId()
  const fieldId = id ?? generatedId
  const listId = `${fieldId}-list`

  const [open, setOpen] = React.useState(false)
  const [active, setActive] = React.useState(NONE_ACTIVE)

  // Clamped on read, never written back: `options` shrinks under the highlight on every
  // keystroke, and a stale index would name an `aria-activedescendant` that is gone.
  const clamped = active >= options.length ? NONE_ACTIVE : active

  const expanded = open && !disabled
  const activeId = clamped === NONE_ACTIVE ? undefined : `${listId}-${String(clamped)}`

  function choose(index: number) {
    const option = options[index]
    if (option === undefined) return
    onSelect(option.value)
    setOpen(false)
    setActive(NONE_ACTIVE)
  }

  function move(delta: number) {
    setOpen(true)
    if (options.length === 0) return
    const next = clamped + delta
    setActive(next < 0 ? options.length - 1 : next % options.length)
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      // Or the caret jumps to the start or the end of the text as the list moves.
      event.preventDefault()
      move(event.key === 'ArrowDown' ? 1 : -1)
      return
    }
    if (event.key === 'Enter' && expanded) {
      // Swallowed whenever the list is open, not only when a row is active: the field lives
      // inside a form, and Enter with nothing highlighted used to submit it mid-search.
      event.preventDefault()
      if (clamped !== NONE_ACTIVE) choose(clamped)
      return
    }
    if (event.key === 'Escape') {
      setOpen(false)
      setActive(NONE_ACTIVE)
    }
  }

  function handleChange(next: string) {
    onInputValueChange(next)
    setOpen(true)
    setActive(NONE_ACTIVE)
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <Label htmlFor={fieldId}>{label}</Label>
      <div className="relative">
        <Input
          id={fieldId}
          role="combobox"
          type="text"
          autoComplete="off"
          aria-expanded={expanded}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => handleChange(event.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={handleKeyDown}
        />
        {expanded ? (
          <ComboboxList
            id={listId}
            label={label}
            options={options}
            active={clamped}
            loading={loading}
            loadingLabel={loadingLabel}
            emptyLabel={emptyLabel}
            onChoose={choose}
            onHover={setActive}
          />
        ) : null}
      </div>
    </div>
  )
}

interface ComboboxListProps {
  readonly id: string
  readonly label: string
  readonly options: readonly ComboboxOption[]
  readonly active: number
  readonly loading: boolean
  readonly loadingLabel: string
  readonly emptyLabel: string
  readonly onChoose: (index: number) => void
  readonly onHover: (index: number) => void
}

function ComboboxList({
  id,
  label,
  options,
  active,
  loading,
  loadingLabel,
  emptyLabel,
  onChoose,
  onHover,
}: ComboboxListProps) {
  // `overflow-y-auto` after the recipe's `overflow-hidden` on purpose: the list scrolls
  // down and still clips a long name sideways rather than escaping the panel.
  const shell = cn(popoverSurface, 'absolute mt-1 max-h-64 w-full overflow-y-auto')

  if (loading || options.length === 0) {
    return (
      // The id lives on this branch too, or the input's `aria-controls` dangles exactly
      // where a screen-reader user needs it: while searching, and on no match.
      <div id={id} className={shell}>
        <p role="status" className="px-3 py-2 text-sm text-muted-foreground">
          {loading ? loadingLabel : emptyLabel}
        </p>
      </div>
    )
  }

  return (
    <ul id={id} role="listbox" aria-label={label} className={cn(shell, 'p-1')}>
      {options.map((option, index) => (
        <li
          key={option.value}
          id={`${id}-${String(index)}`}
          role="option"
          aria-selected={index === active}
          className={cn(
            menuItemBase,
            'items-start px-2 py-1.5 aria-selected:bg-accent aria-selected:text-accent-foreground',
          )}
          // mousedown and not click: the input's blur closes the list first, and the
          // click then lands on nothing.
          onMouseDown={(event) => {
            event.preventDefault()
            onChoose(index)
          }}
          onMouseEnter={() => onHover(index)}
        >
          <span className="flex w-full min-w-0 items-center gap-2">
            {/* Always drawn: without a crest the Thumbnail's own fallback keeps the row's shape. */}
            <Thumbnail src={option.imageUrl} alt="" size="sm" />
            <span className="flex min-w-0 flex-col">
              <span className="break-words">{option.label}</span>
              {option.hint === undefined ? null : (
                <span className="text-note text-muted-foreground">{option.hint}</span>
              )}
            </span>
          </span>
        </li>
      ))}
    </ul>
  )
}
